import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getCompetitors,
  refreshCompetitors as apiRefresh,
  replaceCompetitor as apiReplace,
} from "@/lib/api";
import { RefreshCw, Users, ExternalLink, Loader2, Trophy } from "lucide-react";

function fmtRating(n) {
  return n === null || n === undefined ? "—" : Number(n).toFixed(1);
}

function statusPill(status) {
  if (!status) return null;
  const map = {
    OPERATIONAL: { c: "#6CC24A", label: "Operational" },
    CLOSED_TEMPORARILY: { c: "#EAB308", label: "Temporarily closed" },
    CLOSED_PERMANENTLY: { c: "#EF4444", label: "Closed permanently" },
  };
  const meta = map[status] || { c: "#C7CBD1", label: status };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold border"
      style={{ color: meta.c, borderColor: `${meta.c}55`, background: `${meta.c}0F` }}
    >
      {meta.label}
    </span>
  );
}

export default function CompetitorPanel({ assessment }) {
  const assessmentId = assessment?.assessment_id;
  const business = assessment;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [replacingSlot, setReplacingSlot] = useState(-1);

  useEffect(() => {
    if (!assessmentId) return;
    (async () => {
      setLoading(true);
      try {
        const d = await getCompetitors(assessmentId);
        setData(d);
      } finally {
        setLoading(false);
      }
    })();
  }, [assessmentId]);

  const handleRefresh = async () => {
    setBusy(true);
    try {
      const d = await apiRefresh(assessmentId);
      setData(d);
      toast.success("Competitors refreshed");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not refresh competitors";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleReplace = async (slot) => {
    setReplacingSlot(slot);
    try {
      const r = await apiReplace(assessmentId, slot);
      setData((prev) => {
        const next = { ...(prev || { competitors: [] }) };
        const list = [...(next.competitors || [])];
        list[slot] = r.competitor;
        next.competitors = list;
        return next;
      });
      toast.success("Competitor replaced");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not replace competitor";
      toast.error(msg);
    } finally {
      setReplacingSlot(-1);
    }
  };

  const configured = data?.configured;
  const list = data?.competitors || [];

  return (
    <div className="pp-surface" data-testid="competitors-panel">
      <div className="px-6 py-5 border-b border-[#333C47] flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div className="pp-label mb-1 flex items-center gap-2">
            <Trophy className="h-3.5 w-3.5 text-[#C7CBD1]" /> Local competitors
          </div>
          <div className="font-display text-xl text-white">
            Three real local rivals — verified public signals only.
          </div>
        </div>
        {configured && (
          <button
            onClick={handleRefresh}
            disabled={busy}
            data-testid="competitors-refresh"
            className="pp-btn-secondary text-xs flex items-center gap-2 disabled:opacity-60"
          >
            {busy ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Refreshing
              </>
            ) : (
              <>
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </>
            )}
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-6 pp-label" data-testid="competitors-loading">
          Loading competitors…
        </div>
      ) : !configured ? (
        <div className="p-6 text-sm text-[#C7CBD1] leading-relaxed" data-testid="competitors-not-configured">
          Google integrations are not yet configured. Add{" "}
          <code className="text-white bg-[#0F1115] px-1 border border-[#333C47]">
            GOOGLE_MAPS_API_KEY
          </code>{" "}
          to enable real competitor comparison.
        </div>
      ) : list.length === 0 ? (
        <div className="p-6 text-sm text-[#C7CBD1]">
          No competitors matched yet — click Refresh to search Google Places.
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="competitors-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#C7CBD1] border-b border-[#333C47]">
                <th className="px-5 py-3 font-semibold">Business</th>
                <th className="px-3 py-3 font-semibold">Rating</th>
                <th className="px-3 py-3 font-semibold">Reviews</th>
                <th className="px-3 py-3 font-semibold">Website</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold">Trust Score</th>
                <th className="px-3 py-3 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#333C47] bg-[#232931]">
                <td className="px-5 py-4">
                  <div className="text-white font-medium">
                    {business?.business_name}{" "}
                    <span className="text-[10px] text-[#6CC24A] ml-2 uppercase tracking-wider">
                      · You
                    </span>
                  </div>
                  <div className="text-xs text-[#C7CBD1]">{business?.website}</div>
                </td>
                <td className="px-3 py-4 metric text-white">
                  {fmtRating(business?.signals_enrichment?.place?.rating)}
                </td>
                <td className="px-3 py-4 metric text-white">
                  {business?.signals_enrichment?.place?.user_ratings_total ?? "—"}
                </td>
                <td className="px-3 py-4 text-white">
                  {business?.website ? (
                    <a
                      href={/^https?:/.test(business.website) ? business.website : `https://${business.website}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center gap-1 hover:text-[#6CC24A]"
                    >
                      Visit <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="px-3 py-4">
                  {statusPill(business?.signals_enrichment?.place?.business_status)}
                </td>
                <td className="px-3 py-4 metric text-[#6CC24A] font-bold">
                  {business?.overall_score ?? "—"}
                </td>
                <td className="px-3 py-4 text-right text-[#C7CBD1] text-xs">Baseline</td>
              </tr>
              {list.map((c, i) => {
                if (!c) return null;
                return (
                  <tr key={c.place_id || i} className="border-b border-[#333C47] last:border-0">
                    <td className="px-5 py-4">
                      <div className="text-white font-medium">{c.name || "Unknown"}</div>
                      <div className="text-xs text-[#C7CBD1]">
                        {c.formatted_address || "Address unavailable"}
                      </div>
                    </td>
                    <td className="px-3 py-4 metric text-white">{fmtRating(c.rating)}</td>
                    <td className="px-3 py-4 metric text-white">
                      {c.user_ratings_total ?? "—"}
                    </td>
                    <td className="px-3 py-4 text-white">
                      {c.website ? (
                        <a
                          href={c.website}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-1 hover:text-[#6CC24A]"
                        >
                          Visit <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-[#C7CBD1]">Not listed</span>
                      )}
                    </td>
                    <td className="px-3 py-4">{statusPill(c.business_status)}</td>
                    <td className="px-3 py-4 text-[#C7CBD1] text-xs italic">
                      Not assessed
                    </td>
                    <td className="px-3 py-4 text-right">
                      <button
                        onClick={() => handleReplace(i)}
                        disabled={replacingSlot === i}
                        data-testid={`replace-competitor-${i}`}
                        className="text-xs text-[#C7CBD1] hover:text-white underline underline-offset-2 disabled:opacity-50"
                      >
                        {replacingSlot === i ? "Replacing…" : "Not a rival — replace"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {configured && list.length > 0 && (
        <div className="px-6 py-4 border-t border-[#333C47] text-[11px] text-[#C7CBD1] leading-relaxed">
          Data source: Google Places API. Trust Score comparisons require a full
          Pulse Point assessment of each rival — coming in phase 3.
        </div>
      )}
    </div>
  );
}
